import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, Surface, CopyButton } from "@/components/common";
import { RefinePanel } from "@/components/RefinePanel";
import { AttachmentUploader } from "@/components/AttachmentUploader";
import { LanguageSelect } from "@/components/LanguageSelect";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "subject", label: "Emnefelt" },
  { key: "preview", label: "Preview-tekst" },
  { key: "headline", label: "Overskrift" },
  { key: "intro", label: "Indledning" },
  { key: "body", label: "Brødtekst" },
  { key: "cta", label: "Call To Action" },
];

export default function Newsletter() {
  const location = useLocation();
  const { t, contentLang } = useLocale();
  const [topic, setTopic] = useState("");
  const [result, setResult] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [language, setLanguage] = useState(contentLang);

  useEffect(() => {
    if (location.state?.topic) { setTopic(location.state.topic); generate(location.state.topic); }
    // eslint-disable-next-line
  }, []);

  const generate = async (arg) => {
    const value = typeof arg === "string" ? arg : topic;
    if (!value.trim()) return toast.error(t("Beskriv emnet for nyhedsbrevet"));
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/newsletter", { topic: value, language, attachment_ids: attachments.map((a) => a.id) });
      setResult(data.result);
      setProjectId(data.project_id);
      toast.success(t("Nyhedsbrev klar — gemt automatisk"));
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const allText = result ? FIELDS.map((f) => `${t(f.label)}:\n${result[f.key]}`).join("\n\n") : "";

  return (
    <div>
      <PageHeader overline="Nyhedsbrev" title="Et nyhedsbrev, folk åbner"
        subtitle="Beskriv emnet kort — så bygger vi hele nyhedsbrevet." />

      <div className="space-y-3 mb-8">
        <Textarea data-testid="newsletter-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
          rows={3} placeholder={t("Hvad skal nyhedsbrevet handle om?")} />
        <AttachmentUploader files={attachments} onChange={setAttachments} disabled={loading} />
        <div className="flex flex-wrap items-center gap-3">
          <LanguageSelect value={language} onChange={setLanguage} disabled={loading} testid="newsletter-language" />
          <Button onClick={() => generate()} disabled={loading} className="gap-2" data-testid="newsletter-generate-btn">
            <Sparkles className="h-4 w-4" /> {t("Skriv nyhedsbrev")}
          </Button>
        </div>
      </div>

      {loading && <Spinner label="Skriver jeres nyhedsbrev..." />}
      {result && (
        <div data-testid="newsletter-result">
          <div className="flex justify-end mb-4"><CopyButton text={allText} label="Kopiér hele nyhedsbrevet" testid="newsletter-copy-all" /></div>
          <div className="space-y-4">
            {FIELDS.map((field, i) => (
              <Surface key={field.key} delay={i * 0.05} testid={`newsletter-field-${field.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{field.label}</p>
                  <CopyButton text={result[field.key]} testid={`newsletter-copy-${field.key}`} label="" />
                </div>
                <Textarea value={result[field.key]} onChange={(e) => setResult({ ...result, [field.key]: e.target.value })}
                  rows={field.key === "body" ? 8 : 2} className="border-0 bg-transparent px-0 leading-relaxed resize-none focus-visible:ring-0"
                  data-testid={`newsletter-text-${field.key}`} />
              </Surface>
            ))}
          </div>
          <div className="mt-4">
            <RefinePanel result={result} setResult={setResult} module="newsletter" projectId={projectId} language={language}
              quick={["Kortere", "Mere fængende emnefelt", "Mere personligt", "Stærkere CTA", "Mindre corporate"].map((q) => t(q))} />
          </div>
        </div>
      )}
    </div>
  );
}
